import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api, unwrap, unwrapPage } from '@/api/client';
import type { components, operations } from '@/api/schema';

export type PreguntaItem = components['schemas']['QuestionListItem'];
export type PreguntaDetalle = components['schemas']['QuestionDetail'];
export type PreguntaBody = components['schemas']['QuestionCreateBody'];
export type Especialidad = components['schemas']['Specialty'];
export type ExamenItem = components['schemas']['ExamSummary'];
export type ExamenDetalle = components['schemas']['ExamDetail'];
export type Reporte = components['schemas']['QuestionReport'];
export type SugerenciaIa = components['schemas']['AiQuestion'];
export type FiltrosBanco = NonNullable<operations['getAdminQuestions']['parameters']['query']>;

export function useEspecialidades() {
  return useQuery({
    queryKey: ['especialidades'],
    queryFn: () => unwrap<Especialidad[]>(api.GET('/api/v1/admin/specialties')),
    staleTime: 10 * 60_000,
  });
}

export function useBanco(query: FiltrosBanco) {
  return useQuery({
    queryKey: ['preguntas', 'lista', query],
    queryFn: () =>
      unwrapPage<PreguntaItem[]>(api.GET('/api/v1/admin/questions', { params: { query } })),
    placeholderData: keepPreviousData,
  });
}

export function usePregunta(id: number | null) {
  return useQuery({
    queryKey: ['preguntas', id],
    queryFn: () =>
      unwrap<PreguntaDetalle>(
        api.GET('/api/v1/admin/questions/{id}', { params: { path: { id: String(id) } } }),
      ),
    enabled: id !== null,
  });
}

export function useExamenes(query: { page: number; q?: string }) {
  return useQuery({
    queryKey: ['examenes', 'lista', query],
    queryFn: () =>
      unwrapPage<ExamenItem[]>(
        api.GET('/api/v1/admin/exams', { params: { query: { ...query, pageSize: 50 } } }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useExamen(id: number) {
  return useQuery({
    queryKey: ['examenes', id],
    queryFn: () =>
      unwrap<ExamenDetalle>(
        api.GET('/api/v1/admin/exams/{id}', { params: { path: { id: String(id) } } }),
      ),
  });
}

export function useReportes(query: { page: number; status?: 'open' | 'closed' }) {
  return useQuery({
    queryKey: ['reportes', query],
    queryFn: () =>
      unwrapPage<Reporte[]>(
        api.GET('/api/v1/admin/question-reports', {
          params: { query: { ...query, pageSize: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** "Medicina interna · Cardiología" */
export function nombreEspecialidad(e: Especialidad): string {
  return e.subspecialty ? `${e.name} · ${e.subspecialty}` : e.name;
}

export const CATEGORIAS: Record<string, string> = {
  enarm: 'Reparto ENARM',
  by_specialty: 'Por especialidad',
  custom: 'Personalizado',
  aleatorio: 'Por especialidad',
  personalizado: 'Por especialidad',
};
export const MODOS: Record<string, string> = {
  enarm: 'ENARM (respuestas al final)',
  entrenamiento: 'Entrenamiento (respuesta inmediata)',
};
